import { createClient } from '@supabase/supabase-js'
import { getLocalDateStr } from '../utils/tzUtils'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Hash function for legacy password verification (used during migration period)
export const hashPassword = async (password) => {
  try {
    const encoder = new TextEncoder();
    const saltedPassword = password + 'menuron_salt_2024';
    const data = encoder.encode(saltedPassword);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  } catch (error) {
    console.error('Password hashing failed:', error);
    throw new Error('Password processing failed');
  }
};

// Restaurant services
export const restaurantService = {
  async getRestaurant(slug) {
    const { data, error } = await supabase
      .from('restaurants')
      .select('*')
      .eq('slug', slug)
      .single()
    return { data, error }
  },

  async getMenu(restaurantId) {
  let { data, error } = await supabase
    .from('categories')
    .select(`
      *,
      menu_items (*)
    `)
    .eq('restaurant_id', restaurantId)
    .eq('is_active', true)
    .or('deleted.is.null,deleted.eq.false') // Filter out soft-deleted categories
    .order('display_order')

  // Filter out deleted menu items
  if (data) {
    data = data.map(category => ({
      ...category,
      menu_items: category.menu_items?.filter(item => !item.deleted) || []
    }));
  }

  return { data, error }
},

  async getTables(restaurantId) {
    const { data, error } = await supabase
      .from('restaurant_tables')
      .select('*')
      .eq('restaurant_id', restaurantId)
      .eq('is_active', true)
      .order('table_number')
    return { data, error }
  }
}

// Order services
export const orderService = {
  async createOrder(orderData) {
    const { data, error } = await supabase
      .from('orders')
      .insert(orderData)
      .select()
      .single()
    return { data, error }
  },

  async createOrderItems(orderItems) {
    const { data, error } = await supabase
      .from('order_items')
      .insert(orderItems)
    return { data, error }
  },

  async getOrders(restaurantId, status = null) {
    let query = supabase
      .from('orders')
      .select(`
        *,
        restaurant_tables (table_number, custom_name),
        order_items (
          *,
          menu_items (name, price)
        )
      `)
      .eq('restaurant_id', restaurantId)
      .order('created_at', { ascending: false })

    if (status) {
      query = query.eq('status', status)
    }

    const { data, error } = await query
    return { data, error }
  },

  async updateOrderStatus(orderId, status) {
    const { data, error } = await supabase
      .from('orders')
      .update({ status })
      .eq('id', orderId)
    return { data, error }
  },

  // Waiter confirms a QR order — sets intermediate status + records waiter's name
  async waiterConfirmOrder(orderId, waiterName) {
    const { data, error } = await supabase
      .from('orders')
      .update({ status: 'waiter_confirmed', confirmed_by_name: waiterName })
      .eq('id', orderId)
      .select()
      .single()
    return { data, error }
  },
    async getOrderById(orderId) {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        restaurant_tables (
          id,
          table_number,
          custom_name
        ),
        order_items (
          id,
          quantity,
          unit_price,
          total_price,
          menu_items (
            id,
            name,
            price
          )
        )
      `)
      .eq('id', orderId)
      .single()
    return { data, error }
  }
}

// Menu management services
export const menuService = {
  async createCategory(categoryData) {
    const { data, error } = await supabase
      .from('categories')
      .insert(categoryData)
      .select()
      .single()
    return { data, error }
  },

  async updateCategory(categoryId, updates) {
    const { data, error } = await supabase
      .from('categories')
      .update(updates)
      .eq('id', categoryId)
    return { data, error }
  },

  async deleteCategory(categoryId) {
  const { data, error } = await supabase
    .from('categories')
    .update({ deleted: true })
    .eq('id', categoryId)
  return { data, error }
  },

  async createMenuItem(itemData) {
    const { data, error } = await supabase
      .from('menu_items')
      .insert(itemData)
      .select()
      .single()
    return { data, error }
  },

  async updateMenuItem(itemId, updates) {
    const { data, error } = await supabase
      .from('menu_items')
      .update(updates)
      .eq('id', itemId)
    return { data, error }
  },

  async deleteMenuItem(itemId) {
    const { data, error } = await supabase
      .from('menu_items')
      .delete()
      .eq('id', itemId)
    return { data, error }
  }
}
// Analytics Services
export const analyticsService = {
  async getAnalyticsOverview(restaurantId, startDate, endDate, timezone = 'Asia/Kolkata') {
    const { data, error } = await supabase.rpc('get_analytics_overview', {
      p_restaurant_id: restaurantId,
      p_start: startDate.toISOString(),
      p_end: endDate.toISOString(),
      p_timezone: timezone,
    })
    return { data: data || null, error }
  },

  async getAnalyticsItems(restaurantId, startDate, endDate) {
    const { data, error } = await supabase.rpc('get_analytics_items', {
      p_restaurant_id: restaurantId,
      p_start: startDate.toISOString(),
      p_end: endDate.toISOString(),
    })
    return { data: data || null, error }
  },
}

// ============================================================
// Billing Settings Service (DEV — POS feature)
// ============================================================
export const billingService = {
  /**
   * getSettings — raw DB row (or null).
   * Most callers should prefer getBillingDefaults() for the normalized shape.
   */
  async getSettings(restaurantId) {
    const { data, error } = await supabase
      .from('restaurant_billing_settings')
      .select('*')
      .eq('restaurant_id', restaurantId)
      .maybeSingle()
    return { data, error }
  },

  /**
   * getBillingDefaults — returns a normalized billing config object ready for use
   * in billing screens and override validation. Falls back to zero-state if no
   * settings row exists, so callers never need to null-check.
   *
   * Shape:
   * {
   *   gstEnabled, gstPercent,
   *   serviceChargeEnabled, serviceChargePercent,
   *   defaultDiscountEnabled, defaultDiscountType, defaultDiscountValue
   * }
   */
  async getBillingDefaults(restaurantId) {
    const { data, error } = await supabase
      .from('restaurant_billing_settings')
      .select('gst_enabled, gst_percent, gstin, service_charge_enabled, service_charge_percent, discount_enabled, discount_type, discount_value')
      .eq('restaurant_id', restaurantId)
      .maybeSingle()

    if (error) return { data: null, error }

    // Import normalizer lazily to avoid circular deps
    const { normalizeBillingSettings } = await import('../utils/billingCalc')
    return { data: normalizeBillingSettings(data), error: null }
  },

  async saveSettings(restaurantId, settings) {
    // Upsert — insert on first save, update on subsequent saves
    const { data, error } = await supabase
      .from('restaurant_billing_settings')
      .upsert(
        { restaurant_id: restaurantId, ...settings },
        { onConflict: 'restaurant_id' }
      )
      .select()
      .single()
    return { data, error }
  },

  // Generate a date-based invoice number: {PREFIX}:{DDMMYYYY}{NNNN}
  // e.g. INV:060320260001 — counter resets daily at midnight in the restaurant's timezone.
  // Auto-creates a default settings row if none exists.
  async claimNextInvoiceNumber(restaurantId, timezone = 'Asia/Kolkata') {
    let { data: settings, error: readError } = await supabase
      .from('restaurant_billing_settings')
      .select('invoice_prefix')
      .eq('restaurant_id', restaurantId)
      .maybeSingle()

    if (!settings) {
      const { data: created, error: createError } = await supabase
        .from('restaurant_billing_settings')
        .upsert(
          {
            restaurant_id: restaurantId,
            gst_enabled: false, gst_percent: 0,
            service_charge_enabled: false, service_charge_percent: 0,
            discount_enabled: false, discount_type: 'NONE', discount_value: 0,
            pricing_mode: 'EXCLUSIVE',
            invoice_prefix: 'INV',
          },
          { onConflict: 'restaurant_id' }
        )
        .select('invoice_prefix')
        .single()
      if (createError) return { invoiceNumber: null, error: createError }
      settings = created
    }

    if (readError && !settings) return { invoiceNumber: null, error: readError }

    const prefix = settings.invoice_prefix || 'INV'
    // Use restaurant timezone so the date prefix and daily counter reset at restaurant midnight
    const localDateStr = getLocalDateStr(new Date(), timezone) // 'YYYY-MM-DD'
    const [yyyy, mm, dd] = localDateStr.split('-')
    const datePart = `${dd}${mm}${yyyy}`
    const pattern = `${prefix}:${datePart}`

    // Find the highest daily sequence used today for this restaurant
    const { data: todayInvoices } = await supabase
      .from('orders')
      .select('invoice_number')
      .eq('restaurant_id', restaurantId)
      .like('invoice_number', `${pattern}%`)

    let maxSeq = 0
    for (const row of (todayInvoices || [])) {
      const suffix = row.invoice_number?.slice(pattern.length)
      const seq = parseInt(suffix, 10)
      if (!isNaN(seq) && seq > maxSeq) maxSeq = seq
    }

    const invoiceNumber = `${pattern}${String(maxSeq + 1).padStart(4, '0')}`
    return { invoiceNumber, error: null }
  },
}

// ============================================================
// POS Order Service (DEV — POS feature)
// ============================================================
export const posService = {
  // Create a counter order with items — order number claimed atomically from Supabase (DA prefix)
  async createCounterOrder(restaurantId, { tableId, counterSessionId, orderType, items, notes }) {
    const subtotal = items.reduce((sum, i) => sum + i.unit_price * i.quantity, 0)
    const orderId = crypto.randomUUID()

    const { data: numData, error: numErr } = await supabase.rpc('claim_next_order_number', {
      p_restaurant_id: restaurantId,
      p_prefix: 'DA',
    })
    const order_number = numErr ? `DA${Date.now().toString().slice(-6)}` : numData

    const { error: orderError } = await supabase
      .from('orders')
      .insert({
        id: orderId,
        restaurant_id: restaurantId,
        table_id: tableId || null,
        counter_session_id: counterSessionId || null,
        order_number,
        status: 'confirmed',
        order_source: 'COUNTER',
        order_type: orderType || 'DINE_IN',
        total_amount: parseFloat(subtotal.toFixed(2)),
        notes: notes || null,
      })

    if (orderError) return { data: null, error: orderError }

    const { error: itemsError } = await supabase
      .from('order_items')
      .insert(items.map(i => ({
        order_id: orderId,
        menu_item_id: i.menu_item_id,
        quantity: i.quantity,
        unit_price: parseFloat(i.unit_price.toFixed(2)),
        total_price: parseFloat((i.unit_price * i.quantity).toFixed(2)),
      })))

    if (itemsError) return { data: null, error: itemsError }
    return { data: { id: orderId, order_number }, error: null }
  },

  // Fetch orders for a restaurant. Pass days= to control the window (default 7).
  async getCounterOrders(restaurantId, { days = 7 } = {}) {
    const cutoff = new Date()
    cutoff.setDate(cutoff.getDate() - days)
    if (days <= 1) cutoff.setHours(0, 0, 0, 0) // snap to midnight for today-only queries
    let query = supabase
      .from('orders')
      .select(`
        id, restaurant_id, table_id, counter_session_id, created_at, status, total_amount, order_type, order_source,
        order_number, notes, invoice_number, billed_at, payment_method, paid_at, confirmed_by_name, is_invalid,
        kot_number,
        subtotal, discount_amount, discount_type, gst_amount, service_charge_amount, total_payable,
        original_gst_percent, applied_gst_percent,
        original_service_charge_percent, applied_service_charge_percent,
        overridden_at,
        restaurant_tables (table_number, custom_name),
        order_items (
          id, quantity, unit_price, total_price,
          menu_items (id, name, price)
        )
      `)
      .eq('restaurant_id', restaurantId)
      .gte('created_at', cutoff.toISOString())
      .neq('status', 'cancelled')
      .order('created_at', { ascending: false })

    const { data, error } = await query
    return { data: data || [], error }
  },

  // Fetch all orders in a date range for reporting (includes all statuses except merged)
  async getOrdersForReport(restaurantId, startDate, endDate, managerId = null) {
    const start = new Date(startDate); start.setHours(0, 0, 0, 0);
    const end   = new Date(endDate);   end.setHours(23, 59, 59, 999);
    let query = supabase
      .from('orders')
      .select(`
        id, order_number, invoice_number, created_at, billed_at, paid_at,
        status, is_invalid, order_type, confirmed_by_name,
        total_amount, total_payable, subtotal,
        discount_type, discount_amount,
        gst_amount, applied_gst_percent,
        service_charge_amount, applied_service_charge_percent,
        payment_method, customer_id, customer_name, customer_phone,
        billed_by_manager_id,
        restaurant_tables (table_number, custom_name),
        order_items (id, quantity, unit_price, total_price, menu_items (id, name)),
        managers (id, name)
      `)
      .eq('restaurant_id', restaurantId)
      .neq('status', 'merged')
      .gte('created_at', start.toISOString())
      .lte('created_at', end.toISOString())
      .order('created_at', { ascending: false });

    if (managerId) query = query.eq('billed_by_manager_id', managerId);

    const { data, error } = await query;
    return { data: data || [], error };
  },

  async getManagerActivityReport(restaurantId, startDate, endDate) {
    const start = new Date(startDate); start.setHours(0, 0, 0, 0);
    const end   = new Date(endDate);   end.setHours(23, 59, 59, 999);
    const { data, error } = await supabase
      .from('orders')
      .select(`
        id, invoice_number, billed_at, paid_at, is_invalid,
        total_payable, discount_amount, payment_method,
        billed_by_manager_id
      `)
      .eq('restaurant_id', restaurantId)
      .not('invoice_number', 'is', null)
      .gte('created_at', start.toISOString())
      .lte('created_at', end.toISOString());
    return { data: data || [], error };
  },

  // Fetch all invoiced orders for the last 6 months (for Invoices tab)
  async getInvoices(restaurantId) {
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - 6);
    const { data, error } = await supabase
      .from('orders')
      .select(`
        id, table_id, created_at, status, total_amount, order_source,
        order_number, invoice_number, billed_at, payment_method, paid_at, is_invalid,
        subtotal, discount_amount, discount_type, gst_amount, service_charge_amount, total_payable,
        original_gst_percent, applied_gst_percent,
        original_service_charge_percent, applied_service_charge_percent,
        overridden_at,
        restaurant_tables (table_number, custom_name),
        order_items (
          id, quantity, unit_price, total_price,
          menu_items (id, name, price)
        )
      `)
      .eq('restaurant_id', restaurantId)
      .not('invoice_number', 'is', null)
      .gte('created_at', cutoff.toISOString())
      .order('created_at', { ascending: false });
    return { data: data || [], error };
  },

  // Accept an incoming QR order — moves it from the incoming panel into the active table
  async confirmOrder(orderId) {
    const { data, error } = await supabase
      .from('orders')
      .update({ status: 'confirmed' })
      .eq('id', orderId)
      .select()
      .single()
    return { data, error }
  },

  // Update order status (kitchen flow: pending → confirmed → ready → delivered)
  async updateStatus(orderId, status) {
    const { data, error } = await supabase
      .from('orders')
      .update({ status })
      .eq('id', orderId)
      .select()
      .single()
    return { data, error }
  },

  // Apply billing calculation and write invoice fields to the order.
  // Also logs any manager overrides of GST / service charge vs restaurant defaults.
  async billOrder(orderId, billingData) {
    const updatePayload = {
      subtotal: billingData.taxableAmount,
      discount_amount: billingData.discountAmount,
      discount_type: billingData.discountType || 'NONE',
      gst_amount: billingData.gstAmount,
      service_charge_amount: billingData.serviceChargeAmount,
      total_payable: billingData.totalPayable,
      invoice_number: billingData.invoiceNumber,
      billed_at: new Date().toISOString(),
      total_amount: billingData.totalPayable,
      applied_gst_percent: billingData.appliedGstPercent ?? null,
      applied_service_charge_percent: billingData.appliedScPercent ?? null,
      // Override log fields — populated only when values differ from restaurant defaults
      original_gst_percent: billingData.isOverride ? (billingData.originalGstPercent ?? null) : null,
      original_service_charge_percent: billingData.isOverride ? (billingData.originalScPercent ?? null) : null,
      overridden_at: billingData.isOverride ? new Date().toISOString() : null,
      // Customer CRM link
      customer_id: billingData.customerId || null,
      customer_name: billingData.customerName || null,
      customer_phone: billingData.customerPhone || null,
      // Manager activity tracking
      billed_by_manager_id: billingData.managerId || null,
      // Payment — set in same update when payment is collected at time of billing
      ...(billingData.paymentMethod ? {
        payment_method: billingData.paymentMethod,
        paid_at: new Date().toISOString(),
        status: 'delivered',
      } : {}),
    }

    const { data, error } = await supabase
      .from('orders')
      .update(updatePayload)
      .eq('id', orderId)
      .select()
      .single()
    return { data, error }
  },

  // Replace all order_items for an order and update total_amount
  async updateOrderItems(orderId, items) {
    const { error: delErr } = await supabase.from('order_items').delete().eq('order_id', orderId)
    if (delErr) return { error: delErr }
    if (items.length > 0) {
      const rows = items.map(i => ({
        order_id: orderId,
        menu_item_id: i.menu_item_id,
        quantity: i.quantity,
        unit_price: i.unit_price,
        total_price: parseFloat((i.unit_price * i.quantity).toFixed(2)),
      }))
      const { error: insErr } = await supabase.from('order_items').insert(rows)
      if (insErr) return { error: insErr }
    }
    const total = parseFloat(items.reduce((s, i) => s + i.unit_price * i.quantity, 0).toFixed(2))
    const { error: updErr } = await supabase.from('orders').update({ total_amount: total }).eq('id', orderId)
    return { error: updErr }
  },

  // Mark a secondary order as merged (kept in DB for tracking, hidden from all POS views)
  // Stamps invoice_number so merged orders can be found at print time
  async markAsMerged(orderId, invoiceNumber) {
    const { error } = await supabase.from('orders').update({ status: 'merged', invoice_number: invoiceNumber }).eq('id', orderId)
    return { error }
  },

  // Mark order as paid
  async markPaid(orderId, paymentMethod) {
    const { data, error } = await supabase
      .from('orders')
      .update({
        payment_method: paymentMethod,
        paid_at: new Date().toISOString(),
        status: 'delivered',
      })
      .eq('id', orderId)
      .select()
      .single()
    return { data, error }
  },

  // Soft-delete an unbilled order — marks as cancelled so it's hidden from UI but kept in DB for audit
  async deleteOrder(orderId) {
    const { error } = await supabase.from('orders').update({ status: 'cancelled' }).eq('id', orderId)
    return { error }
  },

  // Mark a billed order/invoice as invalid (voided)
  async markOrderInvalid(orderId) {
    const { error } = await supabase.from('orders').update({ is_invalid: true }).eq('id', orderId)
    return { error }
  },

  // Update the payment method on an already-paid order
  async updatePaymentMethod(orderId, paymentMethod) {
    const { error } = await supabase.from('orders').update({ payment_method: paymentMethod }).eq('id', orderId)
    return { error }
  },

  // KOT number = order number. No separate sequence needed.
  async claimKotNumber(orderId) {
    const { data: ord, error } = await supabase
      .from('orders').select('order_number').eq('id', orderId).single()
    if (error) return { kotNumber: null, error }
    return { kotNumber: ord?.order_number ?? null, error: null }
  },
}

// ─── CRM Service ──────────────────────────────────────────────────────────────
export const crmService = {
  async searchCustomers(restaurantId, query) {
    let req = supabase
      .from('customers')
      .select('id, restaurant_id, name, phone, email, is_whatsapp, discount_type, discount_value, notes, created_at')
      .eq('restaurant_id', restaurantId)
      .order('name');
    const q = (query || '').trim();
    if (q) req = req.or(`name.ilike.%${q}%,phone.ilike.%${q}%`);
    const { data, error } = await req;
    return { data: data || [], error };
  },

  async getCustomers(restaurantId) {
    const { data, error } = await supabase
      .from('customers')
      .select('id, restaurant_id, name, phone, email, is_whatsapp, discount_type, discount_value, notes, created_at')
      .eq('restaurant_id', restaurantId)
      .order('name');
    return { data: data || [], error };
  },

  async createCustomer(restaurantId, { name, phone, email, is_whatsapp, discount_type, discount_value, notes }) {
    const { data, error } = await supabase
      .from('customers')
      .insert({
        restaurant_id: restaurantId,
        name, phone: phone || null, email: email || null,
        is_whatsapp: is_whatsapp || false,
        discount_type: discount_type || 'NONE',
        discount_value: discount_value || 0,
        notes: notes || null,
      })
      .select()
      .single();
    return { data, error };
  },

  async updateCustomer(customerId, fields) {
    const { data, error } = await supabase
      .from('customers')
      .update(fields)
      .eq('id', customerId)
      .select()
      .single();
    return { data, error };
  },

  async deleteCustomer(customerId) {
    const { error } = await supabase.from('customers').delete().eq('id', customerId);
    return { error };
  },

  async getCustomerOrders(customerId) {
    const { data, error } = await supabase
      .from('orders')
      .select(`
        id, order_number, invoice_number, created_at, total_payable, total_amount,
        payment_method, paid_at, discount_type, discount_amount, status, is_invalid,
        order_items(id, quantity, unit_price, menu_items(name))
      `)
      .eq('customer_id', customerId)
      .neq('status', 'merged')
      .order('created_at', { ascending: false });
    return { data: data || [], error };
  },
};

// Authentication Services
export const authService = {
  // Restaurant Owner Registration
  async registerRestaurant(formData) {
    try {
      // Step 1: Create Supabase Auth user for secure authentication
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email.toLowerCase(),
        password: formData.password,
      });

      if (authError) {
        if (authError.message?.includes('already registered')) {
          throw new Error('Email already exists');
        }
        throw authError;
      }

      // Step 2: Also store legacy hash for backward compatibility during migration
      const hashedPassword = await hashPassword(formData.password);

      const { data: owner, error: ownerError } = await supabase
        .from('restaurant_owners')
        .insert({
          email: formData.email.toLowerCase(),
          password_hash: hashedPassword,
          full_name: formData.ownerName,
          phone: formData.phone,
          supabase_auth_id: authData.user?.id || null
        })
        .select()
        .single();

      if (ownerError) {
        if (ownerError.code === '23505') {
          throw new Error('Email already exists');
        }
        throw ownerError;
      }

      const slug = formData.restaurantName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

      const { data: restaurant, error: restaurantError } = await supabase
        .from('restaurants')
        .insert({
          name: formData.restaurantName,
          slug: slug,
          contact_email: formData.email,
          contact_phone: formData.phone,
          address: formData.address,
          owner_id: owner.id
        })
        .select()
        .single();

      if (restaurantError) {
        if (restaurantError.code === '23505') {
          throw new Error('Restaurant name already exists, please choose a different name');
        }
        await supabase.from('restaurant_owners').delete().eq('id', owner.id);
        throw restaurantError;
      }

      // Sign out from the Supabase Auth session created by signUp
      await supabase.auth.signOut();

      return { data: { owner, restaurant }, error: null };
    } catch (error) {
      return { data: null, error: { message: error.message } };
    }
  },

  // Login — handles both restaurant owners and hired managers (same email+password form)
  async loginManager(email, password) {
    try {
      // Step 1: Supabase Auth sign-in
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase(),
        password: password,
      });

      let supabaseAuthUser = null;
      if (!authError && authData?.user) {
        supabaseAuthUser = authData.user;
      }

      // Step 2: Check restaurant_owners table
      let owner = null;

      if (supabaseAuthUser) {
        // Auth succeeded → auth.uid() is set, RLS policy allows the SELECT
        const { data } = await supabase
          .from('restaurant_owners')
          .select(`*, restaurants (*, is_active)`)
          .eq('email', email.toLowerCase())
          .eq('is_active', true)
          .maybeSingle();
        owner = data;
      } else {
        // Auth failed → use SECURITY DEFINER RPC so RLS doesn't block
        // the lookup (covers legacy owners whose supabase_auth_id is not yet set)
        const hashedInputPassword = await hashPassword(password);
        const { data: rows } = await supabase.rpc('authenticate_owner', {
          p_email: email.toLowerCase(),
          p_password_hash: hashedInputPassword,
        });
        if (rows && rows.length > 0) {
          const row = rows[0];
          owner = { ...row.owner_json, restaurants: [row.restaurant_json] };
        }
      }

      if (owner) {
        // ── Owner login path ──────────────────────────────────────────────────

        // Legacy path: auth failed but authenticate_owner returned a valid owner.
        // Create a Supabase Auth account (lazy migration) then sign in.
        if (!supabaseAuthUser) {
          if (!owner.supabase_auth_id) {
            try {
              const { migrateOwnerToSupabaseAuth } = await import('./authMigration');
              const { authUser } = await migrateOwnerToSupabaseAuth(email, password);
              if (authUser) {
                await supabase.rpc('link_owner_to_auth', {
                  p_owner_id: owner.id, p_auth_id: authUser.id,
                });
                owner = { ...owner, supabase_auth_id: authUser.id };
              }
            } catch (migrationError) {
              console.warn('Lazy migration failed, will retry next login:', migrationError);
            }
          }

          const { data: retryAuth } = await supabase.auth.signInWithPassword({
            email: email.toLowerCase(), password,
          }).catch(() => ({ data: null }));
          if (retryAuth?.user) supabaseAuthUser = retryAuth.user;
        }

        // If auth succeeded but supabase_auth_id not yet stored, link it via RPC.
        // (Happens for admin-created accounts that exist in auth.users but were
        // never linked back to the restaurant_owners row.)
        if (supabaseAuthUser && !owner.supabase_auth_id) {
          await supabase.rpc('link_owner_to_auth', {
            p_owner_id: owner.id, p_auth_id: supabaseAuthUser.id,
          });
          owner = { ...owner, supabase_auth_id: supabaseAuthUser.id };
        }

        if (!owner.restaurants || owner.restaurants.length === 0) {
          throw new Error('No restaurant associated with this account');
        }
        if (!owner.restaurants[0].is_active) {
          throw new Error('Restaurant access has been suspended. Please contact support.');
        }

        const sessionToken = this.generateSessionToken();
        const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000);

        const { error: sessionError } = await supabase
          .from('user_sessions')
          .insert({
            user_id: owner.id,
            user_type: 'owner',
            restaurant_id: owner.restaurants[0].id,
            session_token: sessionToken,
            expires_at: expiresAt.toISOString()
          });

        if (sessionError) throw sessionError;

        localStorage.setItem('menuron_session', sessionToken);
        localStorage.setItem('menuron_user_type', 'owner');

        return {
          data: { user: owner, restaurant: owner.restaurants[0], sessionToken, userType: 'owner' },
          error: null
        };
      }

      // Step 3: Not an owner — check managers table
      if (!supabaseAuthUser) {
        // Auth failed and not an owner — invalid credentials
        throw new Error('Invalid email or password');
      }

      const { data: manager } = await supabase
        .from('managers')
        .select(`*, restaurants (*, is_active)`)
        .eq('email', email.toLowerCase())
        .eq('is_active', true)
        .maybeSingle();

      if (!manager) {
        throw new Error('Invalid email or password');
      }

      if (!manager.restaurants?.is_active) {
        throw new Error('Restaurant access has been suspended. Please contact support.');
      }

      // Link supabase_auth_id if not already set
      if (!manager.supabase_auth_id && supabaseAuthUser) {
        await supabase
          .from('managers')
          .update({ supabase_auth_id: supabaseAuthUser.id })
          .eq('id', manager.id);
      }

      const sessionToken = this.generateSessionToken();
      const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000);

      const { error: sessionError } = await supabase
        .from('user_sessions')
        .insert({
          user_id: manager.id,
          user_type: 'staff_manager',
          restaurant_id: manager.restaurant_id,
          session_token: sessionToken,
          expires_at: expiresAt.toISOString()
        });

      if (sessionError) throw sessionError;

      localStorage.setItem('menuron_session', sessionToken);
      localStorage.setItem('menuron_user_type', 'manager');

      return {
        data: {
          user: { ...manager, managerId: manager.id },
          restaurant: manager.restaurants,
          sessionToken,
          userType: 'manager'
        },
        error: null
      };
    } catch (error) {
      return { data: null, error: { message: error.message } };
    }
  },
  // Waiter Login
async loginWaiter(phone, password, restaurantSlug = null) {
  try {
    // Use SECURITY DEFINER RPC — validates credentials server-side, no waiter data exposed without auth
    const hashedInputPassword = await hashPassword(password);
    const { data: rows, error } = await supabase.rpc('authenticate_waiter', {
      p_phone: phone,
      p_restaurant_slug: restaurantSlug || null,
      p_password_hash: hashedInputPassword,
    });

    if (error || !rows || rows.length === 0) {
      throw new Error('Invalid phone number or password');
    }

    const validWaiters = rows.map(r => r.waiter_data);

    // If waiter works at multiple restaurants, return selection options
    if (validWaiters.length > 1 && !restaurantSlug) {
      return {
        data: {
          multipleRestaurants: true,
          waiters: validWaiters
        },
        error: null
      };
    }

    // Single restaurant or specific restaurant selected
    const selectedWaiter = validWaiters[0];

    // Set Supabase Auth session so auth.uid() is populated for RLS policies
    const _waiterEmail = `w_${selectedWaiter.id}@waiter.menuron.app`;
    if (selectedWaiter.supabase_auth_id) {
      const { error: siErr } = await supabase.auth.signInWithPassword({ email: _waiterEmail, password }).catch(e => ({ error: e }));
      if (siErr) console.warn('[waiter-auth] signIn failed:', siErr.message);
    } else {
      // Lazy migration — create auth account then keep session active
      try {
        const { data: authData, error: signUpErr } = await supabase.auth.signUp({ email: _waiterEmail, password });
        console.log('[waiter-auth] signUp result:', { userId: authData?.user?.id, signUpErr: signUpErr?.message });
        let authUserId = authData?.user?.id;
        // When email confirmation is disabled, Supabase v2 returns { user: null, error: null }
        // for already-registered emails (to prevent enumeration). Fall back to signIn.
        if (!authUserId) {
          const { data: si, error: siErr2 } = await supabase.auth.signInWithPassword({ email: _waiterEmail, password });
          console.log('[waiter-auth] signIn (fallback) result:', { userId: si?.user?.id, siErr2: siErr2?.message });
          authUserId = si?.user?.id;
        }
        if (authUserId) {
          // Use SECURITY DEFINER RPC — direct update is blocked by RLS (waiters can't UPDATE their own row)
          const { error: updErr } = await supabase.rpc('link_waiter_to_auth', { p_waiter_id: selectedWaiter.id, p_auth_id: authUserId });
          console.log('[waiter-auth] supabase_auth_id update:', updErr ? updErr.message : 'ok');
        } else {
          console.warn('[waiter-auth] no authUserId — supabase_auth_id not set');
        }
      } catch (e) { console.error('[waiter-auth] migration error:', e); }
    }

    const sessionToken = this.generateSessionToken();
    const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000);

    await supabase
      .from('user_sessions')
      .insert({
        user_id: selectedWaiter.id,
        user_type: 'waiter',
        restaurant_id: selectedWaiter.restaurant_id,
        session_token: sessionToken,
        expires_at: expiresAt.toISOString()
      });

    localStorage.setItem('menuron_session', sessionToken);
    localStorage.setItem('menuron_user_type', 'waiter');

    return { 
      data: { 
        user: selectedWaiter, 
        restaurant: selectedWaiter.restaurants,
        sessionToken,
        userType: 'waiter'
      }, 
      error: null 
    };
  } catch (error) {
    return { data: null, error: { message: error.message } };
  }
},

// Create Waiter Session (for restaurant selection — multi-restaurant flow)
async createWaiterSession(selectedWaiter, password) {
  try {
    // Set up Supabase Auth so auth.uid() is populated for RLS.
    // Each waiter record has a unique ID, so each restaurant gets its own auth account.
    const _waiterEmail = `w_${selectedWaiter.id}@waiter.menuron.app`;
    if (selectedWaiter.supabase_auth_id) {
      await supabase.auth.signInWithPassword({ email: _waiterEmail, password }).catch(() => {});
    } else if (password) {
      try {
        const { data: authData } = await supabase.auth.signUp({ email: _waiterEmail, password });
        let authUserId = authData?.user?.id;
        if (!authUserId) {
          const { data: si } = await supabase.auth.signInWithPassword({ email: _waiterEmail, password });
          authUserId = si?.user?.id;
        }
        if (authUserId) {
          await supabase.rpc('link_waiter_to_auth', { p_waiter_id: selectedWaiter.id, p_auth_id: authUserId });
        }
      } catch (e) { console.error('[waiter-auth] createWaiterSession migration error:', e); }
    }

    const sessionToken = this.generateSessionToken();
    const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000);

    const { error } = await supabase
      .from('user_sessions')
      .insert({
        user_id: selectedWaiter.id,
        user_type: 'waiter',
        restaurant_id: selectedWaiter.restaurant_id,
        session_token: sessionToken,
        expires_at: expiresAt.toISOString()
      });

    if (error) throw error;

    localStorage.setItem('menuron_session', sessionToken);
    localStorage.setItem('menuron_user_type', 'waiter');

    return { 
      data: { 
        user: selectedWaiter, 
        restaurant: selectedWaiter.restaurants,
        sessionToken,
        userType: 'waiter'
      }, 
      error: null 
    };
  } catch (error) {
    return { data: null, error: { message: error.message } };
  }
},

  // Add Waiter
  async addWaiter(restaurantId, waiterData) {
    try {
      // Check if phone number already exists for this restaurant
      const { data: existingWaiter } = await supabase
        .from('waiters')
        .select('id')
        .eq('restaurant_id', restaurantId)
        .eq('phone', waiterData.phone)
        .single();

      if (existingWaiter) {
        throw new Error('A waiter with this phone number already exists');
      }

      const hashedPassword = await hashPassword(waiterData.password);
      
      const { data, error } = await supabase
        .from('waiters')
        .insert({
          restaurant_id: restaurantId,
          name: waiterData.name,
          phone: waiterData.phone,
          password_hash: hashedPassword,
          is_active: true
        })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          throw new Error('Phone number already exists for this restaurant');
        }
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error.message } };
    }
  },

  async updateWaiterStatus(waiterId, isActive) {
    const { error } = await supabase.from('waiters').update({ is_active: isActive }).eq('id', waiterId);
    return { error };
  },

  async deleteWaiter(waiterId) {
    const { error } = await supabase.from('waiters').delete().eq('id', waiterId);
    return { error };
  },

  // Get Restaurant Waiters
  async getRestaurantWaiters(restaurantId) {
    const { data, error } = await supabase
      .from('waiters')
      .select('id, restaurant_id, name, phone, is_active, created_at')
      .eq('restaurant_id', restaurantId)
      .order('created_at', { ascending: false });

    return { data, error };
  },

  // Verify Session
  async verifySession(sessionToken) {
    try {
      if (!sessionToken) throw new Error('No session token');

      // Use SECURITY DEFINER RPC — prevents unauthenticated REST clients from
      // dumping all rows in user_sessions (the table has no SELECT policy).
      const { data: rows, error } = await supabase.rpc('verify_session', { p_token: sessionToken });
      const session = rows?.[0];

      if (error || !session) throw new Error('Invalid or expired session');
      if (session.restaurants?.is_active === false) throw new Error('Restaurant is paused');

      await supabase.auth.getSession();

      const { data: userJson } = await supabase.rpc('get_user_for_session', {
        p_user_id: session.user_id,
        p_user_type: session.user_type,
      });
      let userData = userJson ?? null;
      if (userData && session.user_type === 'staff_manager') {
        userData = { ...userData, managerId: userData.id };
      }

      if (!userData) throw new Error('User not found or inactive');

      // Normalise user_type to app-level roles
      const appUserType = (session.user_type === 'owner' || session.user_type === 'manager')
        ? 'owner'
        : session.user_type === 'staff_manager'
          ? 'manager'
          : 'waiter';

      return {
        data: {
          user: userData,
          restaurant: session.restaurants,
          userType: appUserType,
          sessionToken
        },
        error: null
      };
    } catch (error) {
      localStorage.removeItem('menuron_session');
      localStorage.removeItem('menuron_user_type');
      return { data: null, error: { message: error.message } };
    }
  },

  // Logout
  async logout() {
    const sessionToken = localStorage.getItem('menuron_session');
    if (sessionToken) {
      await supabase
        .from('user_sessions')
        .delete()
        .eq('session_token', sessionToken);
    }

    // Also sign out of Supabase Auth if signed in
    await supabase.auth.signOut();

    localStorage.removeItem('menuron_session');
    localStorage.removeItem('menuron_user_type');
    localStorage.removeItem('shift_started_date');
    return { error: null };
  },

  // Change Password (fixes the btoa bug, verifies current password, syncs with Supabase Auth)
  async changePassword(userId, currentPassword, newPassword) {
    try {
      const { data: owner, error: fetchError } = await supabase
        .from('restaurant_owners')
        .select('password_hash, email, supabase_auth_id')
        .eq('id', userId)
        .single();

      if (fetchError || !owner) {
        throw new Error('Failed to verify current password');
      }

      // Verify current password using SHA-256 hash
      const currentHash = await hashPassword(currentPassword);
      if (currentHash !== owner.password_hash) {
        throw new Error('Current password is incorrect');
      }

      // Hash new password for legacy table
      const newHash = await hashPassword(newPassword);

      const { error: updateError } = await supabase
        .from('restaurant_owners')
        .update({ password_hash: newHash })
        .eq('id', userId);

      if (updateError) throw updateError;

      // If migrated to Supabase Auth, update password there too
      if (owner.supabase_auth_id) {
        try {
          await supabase.auth.signInWithPassword({
            email: owner.email,
            password: currentPassword,
          });
          await supabase.auth.updateUser({ password: newPassword });
          await supabase.auth.signOut();
        } catch (authUpdateError) {
          console.warn('Failed to sync password with Supabase Auth:', authUpdateError);
        }
      }

      return { error: null };
    } catch (error) {
      return { error: { message: error.message } };
    }
  },

  // Get Restaurant Managers (for owner portal)
  async getRestaurantManagers(restaurantId) {
    const { data, error } = await supabase
      .from('managers')
      .select('id, restaurant_id, name, email, is_active, created_at')
      .eq('restaurant_id', restaurantId)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  // Deactivate / reactivate a manager
  async updateManagerStatus(managerId, isActive) {
    const { error } = await supabase
      .from('managers')
      .update({ is_active: isActive })
      .eq('id', managerId);
    return { error };
  },

  // Permanently delete a manager record
  async deleteManager(managerId) {
    const { error } = await supabase
      .from('managers')
      .delete()
      .eq('id', managerId);
    return { error };
  },

  // Helper methods
  generateSessionToken() {
    return btoa(Math.random().toString(36).substring(2) + Date.now().toString(36));
  },

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  isValidPhone(phone) {
    const phoneRegex = /^\+?[\d\s\-\(\)]{10,}$/;
    return phoneRegex.test(phone);
  }
};
